<script setup lang="ts">
import {
  apartmentTypes,
  periods,
  prices2026,
  type PeriodId,
} from '~/utils/pricing2026';
import { usePriceFormatter } from '~/composables/usePriceFormatter';

type Variant = 'summary' | 'full';

withDefaults(
  defineProps<{
    variant?: Variant;
    showToggle?: boolean;
  }>(),
  { variant: 'summary', showToggle: true }
);

const { t } = useI18n();
const localePath = useLocalePath();

const { format: formatPrice, currency } = usePriceFormatter();

type MobileTab = 'high' | 'low';
const mobileTab = ref<MobileTab>('high');

const toggleAriaLabel = computed(
  () =>
    `${t('prices.table.toggle.usd')} / ${t('prices.table.toggle.gel')} / ${t('prices.table.toggle.rub')}`,
);

type MobileMonthGroup = {
  key: 'low' | 'jun' | 'jul' | 'aug' | 'sep';
  monthPeriod: PeriodId;
  isPeak: boolean;
  periods: PeriodId[];
};

const MOBILE_MONTH_GROUPS: MobileMonthGroup[] = [
  { key: 'low', monthPeriod: 'low-season', isPeak: false, periods: ['low-season'] },
  { key: 'jun', monthPeriod: 'jun-1h', isPeak: false, periods: ['jun-1h', 'jun-2h'] },
  { key: 'jul', monthPeriod: 'jul-1h', isPeak: false, periods: ['jul-1h', 'jul-2h'] },
  { key: 'aug', monthPeriod: 'aug', isPeak: true, periods: ['aug'] },
  { key: 'sep', monthPeriod: 'sep-1h', isPeak: false, periods: ['sep-1h', 'sep-2h'] },
];

const visibleMobileMonths = computed<MobileMonthGroup[]>(() =>
  mobileTab.value === 'high'
    ? MOBILE_MONTH_GROUPS.filter((m) => m.key !== 'low')
    : MOBILE_MONTH_GROUPS.filter((m) => m.key === 'low'),
);

const pricesHref = computed(() => localePath('/prices'));
const apartmentsAnchor = computed(() => localePath('/search'));
</script>

<template>
  <!-- ─── SUMMARY (homepage) ──────────────────────────────────────────── -->
  <section
    v-if="variant === 'summary'"
    id="prices"
    class="relative overflow-hidden py-10 md:py-16 bg-slate-50 dark:bg-slate-900"
  >
    <!-- decorative gold hairline ornament, top center -->
    <div
      aria-hidden="true"
      class="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-amber-400/60 to-transparent"
    />
    <!-- subtle radial atmosphere in dark mode -->
    <div
      aria-hidden="true"
      class="pointer-events-none absolute -top-24 left-1/2 h-72 w-72 -translate-x-1/2 rounded-full bg-amber-400/5 blur-3xl dark:bg-amber-400/10"
    />
    <!-- matching bottom blob — seams into TestimonialsCarousel top blob -->
    <div
      aria-hidden="true"
      class="pointer-events-none absolute -bottom-24 left-1/2 h-72 w-72 -translate-x-1/2 rounded-full bg-amber-400/5 blur-3xl dark:bg-amber-400/10"
    />

    <div data-reveal class="container relative">
      <div class="text-center max-w-2xl mx-auto mb-8">
        <div class="flex items-center justify-center gap-3 font-body text-xs uppercase tracking-[0.22em] text-slate-400 dark:text-slate-500">
          <span class="inline-block h-px w-8 bg-amber-400" aria-hidden="true" />
          <span>{{ t('prices.summary.eyebrow') }}</span>
          <span class="inline-block h-px w-8 bg-amber-400" aria-hidden="true" />
        </div>
        <h2 class="mt-4 font-display italic text-3xl sm:text-4xl md:text-5xl text-balance text-slate-900 dark:text-white">
          {{ t('prices.summary.title') }}
        </h2>
      </div>

      <!-- toolbar -->
      <div class="mt-8 flex flex-wrap items-center justify-between gap-4">
        <div
          v-if="showToggle"
          class="flex w-full md:w-auto md:inline-flex rounded-full border border-slate-300/70 dark:border-slate-700 bg-white/60 dark:bg-slate-950/30 p-1 backdrop-blur-sm"
          role="group"
          :aria-label="toggleAriaLabel"
        >
          <button
            type="button"
            :class="[
              'flex-1 md:flex-none px-5 py-1.5 text-sm font-body rounded-full transition duration-300',
              currency === 'USD'
                ? 'bg-slate-900 text-amber-200 shadow-sm dark:bg-amber-400 dark:text-slate-900'
                : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white',
            ]"
            @click="currency = 'USD'"
          >
            {{ t('prices.table.toggle.usd') }}
          </button>
          <button
            type="button"
            :class="[
              'flex-1 md:flex-none px-5 py-1.5 text-sm font-body rounded-full transition duration-300',
              currency === 'GEL'
                ? 'bg-slate-900 text-amber-200 shadow-sm dark:bg-amber-400 dark:text-slate-900'
                : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white',
            ]"
            @click="currency = 'GEL'"
          >
            {{ t('prices.table.toggle.gel') }}
          </button>
          <button
            type="button"
            :class="[
              'flex-1 md:flex-none px-5 py-1.5 text-sm font-body rounded-full transition duration-300',
              currency === 'RUB'
                ? 'bg-slate-900 text-amber-200 shadow-sm dark:bg-amber-400 dark:text-slate-900'
                : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white',
            ]"
            @click="currency = 'RUB'"
          >
            {{ t('prices.table.toggle.rub') }}
          </button>
        </div>

        <!-- Segmented toggle (high vs low season) — uses aria-pressed instead
             of role=tab/tablist because there are no separate tabpanels with
             aria-controls; both views render unconditionally and the buttons
             just swap visibility. ARIA tab roles without aria-controls fail
             axe (`tab-required-children` / `aria-required-attr`). -->
        <div
          class="lg:hidden flex w-full rounded-full border border-slate-300/70 dark:border-slate-700 bg-white/60 dark:bg-slate-950/30 p-1"
          role="group"
          :aria-label="t('prices.table.toggle.season')"
        >
          <button
            type="button"
            :aria-pressed="mobileTab === 'high'"
            :class="[
              'flex-1 px-3 py-1.5 text-xs font-body rounded-full transition duration-300',
              mobileTab === 'high' ? 'bg-red-500 text-white' : 'text-slate-600 dark:text-slate-400',
            ]"
            @click="mobileTab = 'high'"
          >
            {{ t('prices.table.tab.high') }}
          </button>
          <button
            type="button"
            :aria-pressed="mobileTab === 'low'"
            :class="[
              'flex-1 px-3 py-1.5 text-xs font-body rounded-full transition duration-300',
              mobileTab === 'low' ? 'bg-red-500 text-white' : 'text-slate-600 dark:text-slate-400',
            ]"
            @click="mobileTab = 'low'"
          >
            {{ t('prices.table.tab.low') }}
          </button>
        </div>
      </div>

      <!-- desktop table -->
      <div class="hidden lg:block mt-6 overflow-hidden rounded-3xl border border-slate-200 dark:border-slate-700 bg-white/90 dark:bg-slate-800/60 backdrop-blur-sm shadow-[0_1px_0_0_theme(colors.white)_inset] dark:shadow-none">
        <table class="w-full border-collapse text-sm font-body">
          <caption class="sr-only">{{ t('prices.summary.eyebrow') }}</caption>
          <thead>
            <tr class="border-b border-slate-200 dark:border-slate-700 bg-slate-50/80 dark:bg-slate-900/40">
              <th scope="col" class="text-left px-6 py-4 text-[11px] uppercase tracking-[0.2em] font-semibold text-slate-700 dark:text-slate-200">
                {{ t('prices.table.headerType') }}
              </th>
              <th
                v-for="p in periods"
                :key="p"
                scope="col"
                :class="[
                  'px-3 py-4 text-center text-[11px] uppercase tracking-[0.18em] font-semibold',
                  p === 'aug'
                    ? 'relative text-amber-700 dark:text-amber-300 before:absolute before:left-2 before:right-2 before:top-0 before:h-px before:bg-amber-400/70'
                    : 'text-slate-700 dark:text-slate-200',
                ]"
              >
                <span class="block">{{ t(`prices.table.period.${p}.month`) }}</span>
                <span
                  v-if="t(`prices.table.period.${p}.dates`)"
                  class="mt-0.5 block text-[11px] font-normal normal-case tracking-wider opacity-80"
                >
                  {{ t(`prices.table.period.${p}.dates`) }}
                </span>
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(type, rowIdx) in apartmentTypes"
              :key="type"
              :class="[
                'border-t border-slate-100 dark:border-slate-800 transition-colors',
                rowIdx % 2 === 1 ? 'bg-slate-50/40 dark:bg-slate-900/20' : '',
                'hover:bg-amber-50/50 dark:hover:bg-amber-900/5',
              ]"
            >
              <th scope="row" class="text-left px-6 py-5 font-body font-semibold text-sm text-slate-900 dark:text-white">
                {{ t(`prices.table.type.${type}`) }}
              </th>
              <td
                v-for="p in periods"
                :key="p"
                :class="[
                  'px-3 py-5 text-center tabular-nums text-slate-800 dark:text-slate-100',
                  p === 'aug'
                    ? 'relative font-semibold text-amber-900 dark:text-amber-200 bg-amber-50/60 dark:bg-amber-900/10'
                    : '',
                ]"
              >
                {{ formatPrice(prices2026[type][p]) }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- mobile + iPad portrait: month cards stack (table appears at lg+ where it has room) -->
      <ol class="lg:hidden mt-6 space-y-3" role="list">
        <li
          v-for="(m, idx) in visibleMobileMonths"
          :key="`${mobileTab}-${m.key}`"
          :style="{ animationDelay: `${idx * 60}ms` }"
          class="price-card-in"
        >
          <article
            :class="[
              'rounded-2xl border px-4 py-3.5 backdrop-blur-sm',
              m.isPeak
                ? 'border-amber-300/70 dark:border-amber-700/40 bg-gradient-to-br from-amber-50/90 via-white/70 to-amber-50/50 dark:from-amber-900/20 dark:via-slate-900/40 dark:to-amber-900/5 shadow-[0_1px_0_0_rgba(251,191,36,0.15)_inset]'
                : 'border-slate-200 dark:border-slate-700 bg-white/90 dark:bg-slate-800/60',
            ]"
          >
            <header class="flex items-center gap-2 mb-3">
              <span
                v-if="m.isPeak"
                aria-hidden="true"
                class="text-amber-500 text-[11px] leading-none"
              >★</span>
              <span
                :class="[
                  'font-body text-[11px] uppercase tracking-[0.22em] font-semibold',
                  m.isPeak ? 'text-amber-700 dark:text-amber-300' : 'text-slate-700 dark:text-slate-200',
                ]"
              >
                {{ t(`prices.table.period.${m.monthPeriod}.month_full`) }}
              </span>
              <span
                aria-hidden="true"
                :class="[
                  'ml-auto inline-block h-px flex-1 min-w-[12px]',
                  m.isPeak ? 'bg-amber-400/70' : 'bg-slate-200 dark:bg-slate-700',
                ]"
              />
            </header>

            <div
              v-if="m.periods.length > 1"
              class="grid items-baseline gap-x-3 mb-1.5"
              :style="{ gridTemplateColumns: `minmax(0,1fr) repeat(${m.periods.length}, minmax(44px, auto))` }"
            >
              <span aria-hidden="true" />
              <span
                v-for="p in m.periods"
                :key="`h-${p}`"
                class="text-right font-body text-[10px] uppercase tracking-wider text-slate-500 dark:text-slate-400"
              >
                {{ t(`prices.table.period.${p}.dates`) }}
              </span>
            </div>

            <dl class="divide-y divide-slate-100 dark:divide-slate-800/60">
              <div
                v-for="type in apartmentTypes"
                :key="type"
                class="grid items-baseline gap-x-3 py-2 first:pt-0 last:pb-0"
                :style="{ gridTemplateColumns: `minmax(0,1fr) repeat(${m.periods.length}, minmax(44px, auto))` }"
              >
                <dt class="font-body text-sm text-slate-800 dark:text-slate-100 min-w-0">
                  {{ t(`prices.table.type.${type}`) }}
                </dt>
                <dd
                  v-for="p in m.periods"
                  :key="p"
                  :class="[
                    'm-0 text-right font-body text-base tabular-nums font-semibold',
                    m.isPeak ? 'text-amber-900 dark:text-amber-100' : 'text-slate-900 dark:text-white',
                  ]"
                >
                  {{ formatPrice(prices2026[type][p]) }}
                </dd>
              </div>
            </dl>
          </article>
        </li>
      </ol>

      <p class="mt-6 text-xs font-body text-slate-700 dark:text-slate-200">
        {{ t('prices.table.footnote.summary') }}
      </p>

      <div class="mt-8 flex flex-col items-center gap-y-4">
        <a
          :href="apartmentsAnchor"
          class="group flex w-full items-center justify-center gap-3 rounded-full bg-red-500 px-7 py-3.5 text-sm font-body font-semibold uppercase tracking-[0.18em] text-white shadow-lg shadow-red-500/25 transition hover:bg-red-600 hover:shadow-red-500/40"
        >
          {{ t('prices.cta.viewApartments') }}
          <Icon
            name="lucide:arrow-up-right"
            class="size-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
          />
        </a>
        <NuxtLink
          :to="pricesHref"
          class="group inline-flex items-center gap-2 text-sm font-body text-slate-700 dark:text-slate-200 underline-offset-[6px] decoration-amber-400/70 decoration-1 hover:underline hover:text-amber-700 dark:hover:text-amber-300 transition"
        >
          {{ t('prices.summary.seeFullLink') }}
        </NuxtLink>

        <!-- Low-commitment path for visitors still weighing the price. -->
        <BookingAskQuestionCta
          class="mt-2 justify-center"
          placement="home_prices"
        />
      </div>
    </div>
  </section>

  <!-- ─── FULL (used inside /prices) ─────────────────────────────────── -->
  <div v-else class="space-y-6">
    <div class="flex flex-wrap items-center justify-between gap-4">
      <div
        v-if="showToggle"
        class="flex w-full md:w-auto md:inline-flex rounded-full border border-slate-300/70 dark:border-slate-700 bg-white/60 dark:bg-slate-950/30 p-1 backdrop-blur-sm"
        role="group"
        :aria-label="toggleAriaLabel"
      >
        <button
          type="button"
          :class="[
            'flex-1 md:flex-none px-5 py-1.5 text-sm font-body rounded-full transition duration-300',
            currency === 'USD'
              ? 'bg-slate-900 text-amber-200 shadow-sm dark:bg-amber-400 dark:text-slate-900'
              : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white',
          ]"
          @click="currency = 'USD'"
        >
          {{ t('prices.table.toggle.usd') }}
        </button>
        <button
          type="button"
          :class="[
            'flex-1 md:flex-none px-5 py-1.5 text-sm font-body rounded-full transition duration-300',
            currency === 'GEL'
              ? 'bg-slate-900 text-amber-200 shadow-sm dark:bg-amber-400 dark:text-slate-900'
              : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white',
          ]"
          @click="currency = 'GEL'"
        >
          {{ t('prices.table.toggle.gel') }}
        </button>
        <button
          type="button"
          :class="[
            'flex-1 md:flex-none px-5 py-1.5 text-sm font-body rounded-full transition duration-300',
            currency === 'RUB'
              ? 'bg-slate-900 text-amber-200 shadow-sm dark:bg-amber-400 dark:text-slate-900'
              : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white',
          ]"
          @click="currency = 'RUB'"
        >
          {{ t('prices.table.toggle.rub') }}
        </button>
      </div>

      <div
        class="lg:hidden flex w-full rounded-full border border-slate-300/70 dark:border-slate-700 bg-white/60 dark:bg-slate-950/30 p-1"
        role="group"
        :aria-label="t('prices.table.toggle.season')"
      >
        <button
          type="button"
          :aria-pressed="mobileTab === 'high'"
          :class="[
            'flex-1 px-3 py-1.5 text-xs font-body rounded-full transition duration-300',
            mobileTab === 'high' ? 'bg-red-500 text-white' : 'text-slate-600 dark:text-slate-400',
          ]"
          @click="mobileTab = 'high'"
        >
          {{ t('prices.table.tab.high') }}
        </button>
        <button
          type="button"
          :aria-pressed="mobileTab === 'low'"
          :class="[
            'flex-1 px-3 py-1.5 text-xs font-body rounded-full transition duration-300',
            mobileTab === 'low' ? 'bg-red-500 text-white' : 'text-slate-600 dark:text-slate-400',
          ]"
          @click="mobileTab = 'low'"
        >
          {{ t('prices.table.tab.low') }}
        </button>
      </div>
    </div>

    <!-- desktop full table (lg+ only — iPad portrait gets card stack below) -->
    <div class="hidden lg:block overflow-hidden rounded-3xl border border-slate-200 dark:border-slate-700 bg-white/90 dark:bg-slate-800/60 backdrop-blur-sm">
      <table class="w-full border-collapse text-sm font-body">
        <thead>
          <tr class="border-b border-slate-200 dark:border-slate-700 bg-slate-50/80 dark:bg-slate-900/40">
            <th scope="col" class="text-left px-6 py-4 text-[11px] uppercase tracking-[0.2em] font-semibold text-slate-700 dark:text-slate-200">
              {{ t('prices.table.headerType') }}
            </th>
            <th
              v-for="p in periods"
              :key="p"
              scope="col"
              :class="[
                'px-3 py-4 text-center text-[11px] uppercase tracking-[0.18em] font-semibold',
                p === 'aug'
                  ? 'relative text-amber-700 dark:text-amber-300 before:absolute before:left-2 before:right-2 before:top-0 before:h-px before:bg-amber-400/70'
                  : 'text-slate-700 dark:text-slate-200',
              ]"
            >
              <span class="block">{{ t(`prices.table.period.${p}.month`) }}</span>
                <span
                  v-if="t(`prices.table.period.${p}.dates`)"
                  class="mt-0.5 block text-[11px] font-normal normal-case tracking-wider opacity-80"
                >
                  {{ t(`prices.table.period.${p}.dates`) }}
                </span>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="(type, rowIdx) in apartmentTypes"
            :key="type"
            :class="[
              'border-t border-slate-100 dark:border-slate-800 transition-colors',
              rowIdx % 2 === 1 ? 'bg-slate-50/40 dark:bg-slate-900/20' : '',
              'hover:bg-amber-50/50 dark:hover:bg-amber-900/5',
            ]"
          >
            <th scope="row" class="text-left px-6 py-5 font-display italic text-lg text-slate-900 dark:text-white">
              {{ t(`prices.table.type.${type}`) }}
            </th>
            <td
              v-for="p in periods"
              :key="p"
              :class="[
                'px-3 py-5 text-center tabular-nums text-slate-800 dark:text-slate-100',
                p === 'aug'
                  ? 'relative font-semibold text-amber-900 dark:text-amber-200 bg-amber-50/60 dark:bg-amber-900/10'
                  : '',
              ]"
            >
              {{ formatPrice(prices2026[type][p]) }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- mobile + iPad portrait: month cards stack -->
    <ol class="lg:hidden space-y-3" role="list">
      <li
        v-for="(m, idx) in visibleMobileMonths"
        :key="`${mobileTab}-${m.key}`"
        :style="{ animationDelay: `${idx * 60}ms` }"
        class="price-card-in"
      >
        <article
          :class="[
            'rounded-2xl border px-4 py-4 backdrop-blur-sm',
            m.isPeak
              ? 'border-amber-300/70 dark:border-amber-700/40 bg-gradient-to-br from-amber-50/90 via-white/70 to-amber-50/50 dark:from-amber-900/20 dark:via-slate-900/40 dark:to-amber-900/5 shadow-[0_1px_0_0_rgba(251,191,36,0.15)_inset]'
              : 'border-slate-200 dark:border-slate-700 bg-white/90 dark:bg-slate-800/60',
          ]"
        >
          <header class="flex items-center gap-2 mb-3">
            <span
              v-if="m.isPeak"
              aria-hidden="true"
              class="text-amber-500 text-[11px] leading-none"
            >★</span>
            <span
              :class="[
                'font-body text-[11px] uppercase tracking-[0.22em] font-semibold',
                m.isPeak ? 'text-amber-700 dark:text-amber-300' : 'text-slate-700 dark:text-slate-200',
              ]"
            >
              {{ t(`prices.table.period.${m.monthPeriod}.month_full`) }}
            </span>
            <span
              aria-hidden="true"
              :class="[
                'ml-auto inline-block h-px flex-1 min-w-[12px]',
                m.isPeak ? 'bg-amber-400/70' : 'bg-slate-200 dark:bg-slate-700',
              ]"
            />
          </header>

          <div
            v-if="m.periods.length > 1"
            class="grid items-baseline gap-x-3 mb-1.5"
            :style="{ gridTemplateColumns: `minmax(0,1fr) repeat(${m.periods.length}, minmax(44px, auto))` }"
          >
            <span aria-hidden="true" />
            <span
              v-for="p in m.periods"
              :key="`h-${p}`"
              class="text-right font-body text-[10px] uppercase tracking-wider text-slate-500 dark:text-slate-400"
            >
              {{ t(`prices.table.period.${p}.dates`) }}
            </span>
          </div>

          <dl class="divide-y divide-slate-100 dark:divide-slate-800/60">
            <div
              v-for="type in apartmentTypes"
              :key="type"
              class="grid items-baseline gap-x-3 py-2.5 first:pt-0 last:pb-0"
              :style="{ gridTemplateColumns: `minmax(0,1fr) repeat(${m.periods.length}, minmax(44px, auto))` }"
            >
              <dt class="font-display italic text-base text-slate-800 dark:text-slate-100 min-w-0">
                {{ t(`prices.table.type.${type}`) }}
              </dt>
              <dd
                v-for="p in m.periods"
                :key="p"
                :class="[
                  'm-0 text-right font-body text-base tabular-nums font-semibold',
                  m.isPeak ? 'text-amber-900 dark:text-amber-100' : 'text-slate-900 dark:text-white',
                ]"
              >
                {{ formatPrice(prices2026[type][p]) }}
              </dd>
            </div>
          </dl>
        </article>
      </li>
    </ol>

    <p class="text-xs font-body text-slate-700 dark:text-slate-200">
      {{ t('prices.table.footnote.full') }}
    </p>
  </div>
</template>

<style scoped>
@keyframes price-card-in {
  from { opacity: 0; transform: translateY(6px); }
  to { opacity: 1; transform: none; }
}
.price-card-in {
  animation: price-card-in 0.45s cubic-bezier(0.22, 1, 0.36, 1) backwards;
}
@media (prefers-reduced-motion: reduce) {
  .price-card-in { animation: none; }
}
</style>
